/**
 * Logger utility for error tracking and debugging
 */

const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

/**
 * Log error with structured data
 * @param {string} message - Error message
 * @param {Object} data - Additional error data
 */
export const logError = (message, data = {}) => {
  const errorLog = {
    level: LOG_LEVELS.ERROR,
    message,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...data
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error Log:', errorLog);
  }

  // Here you could send to an error tracking service like Sentry
  // Example: Sentry.captureException(new Error(message), { extra: errorLog });
  
  // Store in localStorage for debugging (with rotation)
  storeLocalLog(errorLog);
};

/**
 * Log warning with structured data
 * @param {string} message - Warning message
 * @param {Object} data - Additional warning data
 */
export const logWarning = (message, data = {}) => {
  const warningLog = {
    level: LOG_LEVELS.WARN,
    message,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    ...data
  };

  if (process.env.NODE_ENV === 'development') {
    console.warn('Warning Log:', warningLog);
  }

  storeLocalLog(warningLog);
};

/**
 * Log info with structured data
 * @param {string} message - Info message
 * @param {Object} data - Additional info data
 */
export const logInfo = (message, data = {}) => {
  const infoLog = {
    level: LOG_LEVELS.INFO,
    message,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    ...data
  };

  if (process.env.NODE_ENV === 'development') {
    console.info('Info Log:', infoLog);
  }

  storeLocalLog(infoLog);
};

/**
 * Store log in localStorage with rotation
 * @param {Object} logEntry - Log entry to store
 */
const storeLocalLog = (logEntry) => {
  try {
    const maxLogs = 100; // Keep last 100 logs
    const logsKey = 'solosocial_logs';
    
    let logs = [];
    try {
      logs = JSON.parse(localStorage.getItem(logsKey) || '[]');
    } catch (e) {
      logs = [];
    }

    logs.push(logEntry);
    
    // Keep only the most recent logs
    if (logs.length > maxLogs) {
      logs = logs.slice(-maxLogs);
    }

    localStorage.setItem(logsKey, JSON.stringify(logs));
  } catch (e) {
    // localStorage might be full or unavailable
    console.warn('Failed to store log locally:', e);
  }
};

/**
 * Get stored logs from localStorage
 * @param {string} level - Optional log level filter
 * @returns {Array} Array of log entries
 */
export const getLogs = (level = null) => {
  try {
    const logs = JSON.parse(localStorage.getItem('solosocial_logs') || '[]');
    return level ? logs.filter(log => log.level === level) : logs;
  } catch (e) {
    return [];
  }
};

/**
 * Clear stored logs
 */
export const clearLogs = () => {
  try {
    localStorage.removeItem('solosocial_logs');
  } catch (e) {
    console.warn('Failed to clear logs:', e);
  }
};

/**
 * Log performance metrics
 * @param {string} operation - Operation name
 * @param {number} duration - Duration in milliseconds
 * @param {Object} data - Additional performance data
 */
export const logPerformance = (operation, duration, data = {}) => {
  const perfLog = {
    level: 'performance',
    operation,
    duration,
    timestamp: new Date().toISOString(),
    ...data
  };

  if (process.env.NODE_ENV === 'development') {
    console.log(`Performance: ${operation} took ${duration}ms`, perfLog);
  }

  storeLocalLog(perfLog);
};

/**
 * Track API call for debugging and monitoring
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {number} status - HTTP status code
 * @param {number} duration - Request duration in milliseconds
 * @param {Object} data - Additional tracking data
 */
export const logApiCall = (endpoint, method, status, duration, data = {}) => {
  const apiLog = {
    level: 'api',
    endpoint,
    method,
    status,
    duration,
    timestamp: new Date().toISOString(),
    ...data
  };

  if (process.env.NODE_ENV === 'development') {
    console.log(`API: ${method} ${endpoint} - ${status} (${duration}ms)`, apiLog);
  }

  storeLocalLog(apiLog);
};

export default {
  logError,
  logWarning,
  logInfo,
  logPerformance,
  logApiCall,
  getLogs,
  clearLogs
};